import { useNavigate } from "react-router-dom";
import { Helmet } from 'react-helmet-async';
import Header from "../components/feature/Header";
import MobileHeader from "../components/feature/MobileHeader";
import Footer from "../components/feature/Footer";
import firebase from "firebase/app";
import "firebase/auth";
import { analytics } from "../firebase-config";

export default function NewLanding() {
    analytics.logEvent('page_view', {
        page_title: 'new_landing'
    });
    
    const navigate = useNavigate();
    
    const gotoHompage = async () => {
        const userId = await firebase.auth().currentUser?.uid;
        if (userId) {
            navigate('/view-current-prices');
        } else {
            localStorage.setItem('pendingNavigation', '/view-current-prices');
            navigate('/view-prices-guest');
        }
    }

    const gotoSignup = () => {
        navigate('/signup');
    }

    const gotoAIAssistant = async () => {
        navigate('/ai-assistant');
        /* const userId = await firebase.auth().currentUser?.uid;
        if (userId) {
            navigate('/ai-assistant');
        } else {
            localStorage.setItem('pendingNavigation', '/ai-assistant');
            navigate('/login');
        } */
    }

    return (
        <div className="min-h-screen w-full font-worksans">
            <Helmet>
                <title>ArketData - AI-Powered Food Price Analytics & Smart Shopping Assistant</title>
                <meta
                    name="description"
                    content="Meet your intelligent food price analytics assistant. Get AI-powered insights, real-time price comparisons, and smart shopping recommendations across Nigerian markets."
                />
                <meta
                    name="keywords"
                    content="AI assistant, Food prices, Smart shopping, Price analytics, Arketdata, Market intelligence, Nigeria markets"
                />
            </Helmet>

            <div className="lg:block hidden">
                <Header />
            </div>
            <div className="lg:hidden block">
                <MobileHeader />
            </div>

            {/* Hero Section */}
            <div className="bg-gradient-to-r from-green-600 to-green-800 py-20">
                <div className="container mx-auto px-4 text-center">
                    <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
                        Meet Your AI-Powered Food Price Analytics Assistant
                    </h1>
                    <p className="text-xl md:text-2xl text-white mb-8 max-w-4xl mx-auto">
                        Get intelligent insights, smart shopping recommendations, and real-time price analytics across Nigerian markets. Your AI assistant makes food shopping smarter and more affordable.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <button 
                            onClick={gotoAIAssistant}
                            className="bg-white text-green-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors text-lg"
                        >
                            🤖 Try AI Assistant Now
                        </button>
                        <button 
                            onClick={gotoHompage}
                            className="bg-transparent border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-green-600 transition-colors"
                        >
                            Compare Markets
                        </button>
                        <button 
                            onClick={gotoSignup}
                            className="bg-transparent border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-green-600 transition-colors"
                        >
                            Sign Up Free
                        </button>
                    </div>
                </div>
            </div>

            {/* AI Assistant Feature Section */}
            <div className="py-16 bg-white">
                <div className="container mx-auto px-4">
                    <h2 className="text-3xl font-bold text-center mb-12">Your Intelligent Food Price Analytics Assistant</h2>
                    
                    <div className="max-w-4xl mx-auto bg-gradient-to-r from-green-50 to-blue-50 rounded-2xl p-8 mb-12">
                        <div className="grid md:grid-cols-2 gap-8 items-center">
                            <div>
                                <h3 className="text-2xl font-semibold mb-4">🤖 Chat with your AI Assistant</h3>
                                <p className="text-gray-600 mb-6">
                                    Ask questions in natural language and get intelligent insights about food prices, market trends, and smart shopping recommendations.
                                </p>
                                <div className="bg-white p-4 rounded-lg shadow-sm mb-4">
                                    <p className="text-sm text-gray-500 mb-2">Try asking:</p>
                                    <ul className="text-sm space-y-1">
                                        <li>• "What's the cheapest market for rice in Lagos?"</li>
                                        <li>• "How have tomato prices changed this month?"</li>
                                        <li>• "Recommend the best time to buy beans"</li>
                                    </ul>
                                </div>
                                <button 
                                    onClick={gotoAIAssistant}
                                    className="bg-green-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors"
                                >
                                    Start Chatting with AI →
                                </button>
                            </div>
                            <div>
                                <img 
                                    src="https://images.pexels.com/photos/8386440/pexels-photo-8386440.jpeg?auto=compress&cs=tinysrgb&w=800" 
                                    alt="AI Assistant Interface" 
                                    className="w-full h-64 object-cover rounded-lg shadow-lg"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        <div className="text-center">
                            <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                                <span className="text-2xl">🧠</span>
                            </div>
                            <h3 className="text-xl font-semibold mb-3">Smart Insights</h3>
                            <p className="text-gray-600">
                                Get AI-powered analysis of price trends, seasonal patterns, and market predictions to make informed decisions.
                            </p>
                        </div>
                        <div className="text-center">
                            <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                                <span className="text-2xl">💬</span>
                            </div>
                            <h3 className="text-xl font-semibold mb-3">Natural Language</h3>
                            <p className="text-gray-600">
                                Ask questions in plain English and get clear, actionable answers about food prices and market conditions.
                            </p>
                        </div>
                        <div className="text-center">
                            <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                                <span className="text-2xl">📊</span>
                            </div>
                            <h3 className="text-xl font-semibold mb-3">Personalized Recommendations</h3>
                            <p className="text-gray-600">
                                Receive tailored shopping suggestions based on your preferences, location, and budget constraints.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Compare Food Prices Section */}
            <div className="py-16 bg-gray-50">
                <div className="container mx-auto px-4">
                    <h2 className="text-3xl font-bold text-center mb-12">Advanced Price Analytics & Comparison</h2>
                    
                    <div className="grid md:grid-cols-2 gap-8">
                        <div className="bg-white p-8 rounded-lg shadow-md">
                            <h3 className="text-xl font-semibold mb-4">AI-Powered Market Analysis</h3>
                            <img 
                                src="https://images.pexels.com/photos/7148047/pexels-photo-7148047.jpeg?auto=compress&cs=tinysrgb&w=800" 
                                alt="AI market analysis" 
                                className="w-full h-48 object-cover rounded-lg mb-4"
                            />
                            <p className="text-gray-600 mb-4">
                                Our AI analyzes price patterns across all Nigerian markets in real-time. Get intelligent insights on staples like rice, beans, yam, and tomatoes with predictive analytics.
                            </p>
                            <p className="text-gray-600">
                                Discover price trends, seasonal patterns, and market opportunities with AI-driven recommendations tailored to your shopping needs.
                            </p>
                        </div>

                        <div className="bg-white p-8 rounded-lg shadow-md">
                            <h3 className="text-xl font-semibold mb-4">Smart Filtering & Search</h3>
                            <img 
                                src="https://images.pexels.com/photos/5632402/pexels-photo-5632402.jpeg?auto=compress&cs=tinysrgb&w=800" 
                                alt="Smart filtering" 
                                className="w-full h-48 object-cover rounded-lg mb-4"
                            />
                            <p className="text-gray-600 mb-4">AI-enhanced search with intelligent filters:</p>
                            <ul className="list-disc list-inside text-gray-600 space-y-2">
                                <li>Nigerian states and local markets</li>
                                <li>Food categories (grains, tubers, vegetables, proteins)</li>
                                <li>AI-predicted price trends (rising, falling, stable)</li>
                                <li>Market types (local markets, supermarkets, wholesale)</li>
                                <li>Budget-based recommendations</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            {/* AI-Powered Price Intelligence Section */}
            <div className="py-16 bg-white">
                <div className="container mx-auto px-4">
                    <h2 className="text-3xl font-bold text-center mb-12">AI-Powered Price Intelligence</h2>
                    
                    <div className="grid md:grid-cols-3 gap-8">
                        <div className="text-center">
                            <img 
                                src="https://images.pexels.com/photos/590016/pexels-photo-590016.jpeg?auto=compress&cs=tinysrgb&w=800" 
                                alt="Live AI analysis" 
                                className="w-full h-48 object-cover rounded-lg mb-4"
                            />
                            <h3 className="text-xl font-semibold mb-3">Live AI Analysis</h3>
                            <p className="text-gray-600">
                                Our AI continuously analyzes price data from Nigerian markets, providing real-time insights and intelligent recommendations.
                            </p>
                        </div>

                        <div className="text-center">
                            <img 
                                src="https://images.pexels.com/photos/186461/pexels-photo-186461.jpeg?auto=compress&cs=tinysrgb&w=800" 
                                alt="Predictive analytics" 
                                className="w-full h-48 object-cover rounded-lg mb-4"
                            />
                            <h3 className="text-xl font-semibold mb-3">Predictive Analytics</h3>
                            <p className="text-gray-600">
                                AI algorithms analyze historical patterns to predict future price movements and identify the best buying opportunities.
                            </p>
                        </div>

                        <div className="text-center">
                            <img 
                                src="https://images.pexels.com/photos/4386431/pexels-photo-4386431.jpeg?auto=compress&cs=tinysrgb&w=800" 
                                alt="Smart alerts" 
                                className="w-full h-48 object-cover rounded-lg mb-4"
                            />
                            <h3 className="text-xl font-semibold mb-3">Smart Alerts</h3>
                            <p className="text-gray-600">
                                AI-powered notifications that learn your preferences and alert you about significant price changes and shopping opportunities.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* AI Analytics Features Section */}
            <div className="py-16 bg-gray-50">
                <div className="container mx-auto px-4">
                    <h2 className="text-3xl font-bold text-center mb-12">AI Analytics Features</h2>
                    
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                        <div className="bg-white p-6 rounded-lg shadow-md">
                            <h3 className="text-lg font-semibold mb-3">🌾 Seasonal Patterns</h3>
                            <p className="text-gray-600">
                                AI analyzes seasonal trends for Nigerian crops, helping you understand harvest cycles and price fluctuations.
                            </p>
                        </div>

                        <div className="bg-white p-6 rounded-lg shadow-md">
                            <h3 className="text-lg font-semibold mb-3">🚛 Supply Chain Intelligence</h3>
                            <p className="text-gray-600">
                                Track how transportation costs, fuel prices, and logistics affect food prices across Nigerian markets.
                            </p>
                        </div>

                        <div className="bg-white p-6 rounded-lg shadow-md">
                            <h3 className="text-lg font-semibold mb-3">📈 Market Trends</h3>
                            <p className="text-gray-600">
                                AI identifies emerging price trends and market opportunities before they become obvious to other buyers.
                            </p>
                        </div>

                        <div className="bg-white p-6 rounded-lg shadow-md">
                            <h3 className="text-lg font-semibold mb-3">💰 Budget Optimization</h3>
                            <p className="text-gray-600">
                                Get AI-powered recommendations to maximize your purchasing power and stretch your food budget further.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Featured Market Section */}
            <div className="py-16 bg-white">
                <div className="container mx-auto px-4">
                    <div className="text-center mb-8">
                        <h2 className="text-3xl font-bold mb-4">Featured Market: Mile 12 International Market, Lagos</h2>
                        <img 
                            src="https://images.pexels.com/photos/1058277/pexels-photo-1058277.jpeg?auto=compress&cs=tinysrgb&w=1200" 
                            alt="Nigerian Market" 
                            className="w-full max-w-4xl mx-auto h-96 object-cover rounded-lg"
                        />
                        <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
                            One of Nigeria's largest food markets, where our AI tracks thousands of daily price updates. 
                            Get intelligent insights on the best times to buy, seasonal patterns, and price predictions.
                        </p>
                    </div>
                </div>
            </div>

            {/* User-Contributed Price Data Section */}
            <div className="py-16 bg-gray-50">
                <div className="container mx-auto px-4">
                    <h2 className="text-3xl font-bold text-center mb-12">User-Contributed Price Data</h2>
                    
                    <div className="grid md:grid-cols-3 gap-8">
                        <div className="bg-white p-8 rounded-lg shadow-md text-center">
                            <img 
                                src="https://images.pexels.com/photos/4386476/pexels-photo-4386476.jpeg?auto=compress&cs=tinysrgb&w=800" 
                                alt="Submit local prices" 
                                className="w-full h-48 object-cover rounded-lg mb-4"
                            />
                            <h3 className="text-xl font-semibold mb-3">Submit Local Prices</h3>
                            <p className="text-gray-600">
                                Use our mobile app to scan receipts or manually input prices from your local stores and markets. Each submission helps build our hyperlocal database.
                            </p>
                        </div>

                        <div className="bg-white p-8 rounded-lg shadow-md text-center">
                            <img 
                                src="https://images.pexels.com/photos/5428833/pexels-photo-5428833.jpeg?auto=compress&cs=tinysrgb&w=800" 
                                alt="Verification process" 
                                className="w-full h-48 object-cover rounded-lg mb-4"
                            />
                            <h3 className="text-xl font-semibold mb-3">Verification Process</h3>
                            <p className="text-gray-600">
                                Our system cross-references submissions with other local data points. Multiple matches increase confidence scores and validate price accuracy.
                            </p>
                        </div>

                        <div className="bg-white p-8 rounded-lg shadow-md text-center">
                            <img 
                                src="https://images.pexels.com/photos/8721342/pexels-photo-8721342.jpeg?auto=compress&cs=tinysrgb&w=800" 
                                alt="Earn rewards" 
                                className="w-full h-48 object-cover rounded-lg mb-4"
                            />
                            <h3 className="text-xl font-semibold mb-3">Earn Rewards</h3>
                            <p className="text-gray-600">
                                Gain points for verified submissions that contribute to our database. Redeem rewards for premium features, market analysis reports, or partner discounts.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* AI-Powered Features Section */}
            <div className="py-16 bg-white">
                <div className="container mx-auto px-4">
                    <h2 className="text-3xl font-bold text-center mb-12">Why Choose Our AI Assistant?</h2>
                    
                    <div className="grid md:grid-cols-3 gap-8">
                        <div className="bg-gray-50 p-8 rounded-lg">
                            <h3 className="text-xl font-semibold mb-4">🎯 Nigerian Market Expert</h3>
                            <p className="text-gray-600">
                                Our AI is specifically trained on Nigerian food markets, understanding local patterns, seasonal cycles, and regional price variations across all 36 states.
                            </p>
                        </div>

                        <div className="bg-gray-50 p-8 rounded-lg">
                            <h3 className="text-xl font-semibold mb-4">💡 Smart Shopping Insights</h3>
                            <p className="text-gray-600">
                                Get personalized recommendations on when to buy, where to shop, and how to maximize your food budget based on AI analysis of market trends.
                            </p>
                        </div>

                        <div className="bg-gray-50 p-8 rounded-lg">
                            <h3 className="text-xl font-semibold mb-4">🔮 Predictive Analytics</h3>
                            <p className="text-gray-600">
                                AI algorithms forecast price movements, helping you plan purchases and avoid price spikes before they happen.
                            </p>
                        </div>
                    </div>

                    <div className="text-center mt-12">
                        <div className="bg-gradient-to-r from-green-100 to-blue-100 p-6 rounded-lg max-w-2xl mx-auto">
                            <h3 className="text-xl font-semibold mb-3">Ready to Experience AI-Powered Shopping?</h3>
                            <p className="text-gray-600 mb-4">
                                Join thousands of Nigerians who are already using our AI assistant to make smarter food purchases.
                            </p>
                            <button 
                                onClick={gotoAIAssistant}
                                className="bg-green-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors"
                            >
                                🚀 Launch AI Assistant
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Statistics Section */}
            <div className="py-16 bg-green-600 text-white">
                <div className="container mx-auto px-4">
                    <h2 className="text-3xl font-bold text-center mb-12">Powerful AI Analytics at Your Fingertips</h2>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
                        <div>
                            <div className="text-4xl font-bold mb-2">200+</div>
                            <div className="text-lg">Nigerian Markets</div>
                            <div className="text-sm opacity-80">Comprehensive coverage across all 36 states</div>
                        </div>

                        <div>
                            <div className="text-4xl font-bold mb-2">24/7</div>
                            <div className="text-lg">AI Analysis</div>
                            <div className="text-sm opacity-80">Continuous price monitoring and insights</div>
                        </div>

                        <div>
                            <div className="text-4xl font-bold mb-2">10K+</div>
                            <div className="text-lg">Smart Users</div>
                            <div className="text-sm opacity-80">Nigerians using AI for smarter shopping</div>
                        </div>

                        <div>
                            <div className="text-4xl font-bold mb-2">95%</div>
                            <div className="text-lg">Accuracy Rate</div>
                            <div className="text-sm opacity-80">AI predictions and price recommendations</div>
                        </div>
                    </div>

                    <div className="text-center mt-12">
                        <button 
                            onClick={gotoAIAssistant}
                            className="bg-white text-green-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors text-lg"
                        >
                            🤖 Start Using AI Assistant
                        </button>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <div className="py-16">
                <Footer />
            </div>
        </div>
    );
} 